import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {StepComponent} from "../../step/step.component";
import {Step, StepService} from "../../services/step.service";
import {Router} from "@angular/router";
import {ElementService} from "../../services/element.service";
import {PortalService} from "../../services/portal.service";
import {HeizkostenabrechnungForm} from "./heizkostenabrechnung.form";
import {HeizcheckAnfrage} from "../anfrage/heizcheck.anfrage";
import {HeizcheckRequestService} from "../services/heizcheck-request.service";
import {FormModel} from "../../form/form.model.abstract";
import {TranslateService} from "@ngx-translate/core";
import {HeizcheckConstants} from "../heizcheck.constants";
import {EnergyMapService} from '../../energy-map/services/energy.map.service';

/**
 * Basic steps for the input chapter of apartments
 *
 * @inheritDoc
 */
@Component({
    selector: 'app-step-heizkostenabrechnung',
    templateUrl: '../../step/step.component.html',
    standalone: false
})
export class HeizkostenabrechnungStepComponent extends StepComponent implements OnInit {

  /**
   * (input) data of the user
   */
  anfrage: HeizcheckAnfrage;

  /**
   * @inheritDoc
   */
  formConfig: HeizkostenabrechnungForm;

  /**
   * Overwrite the label prefix.
   */
  textkeyPrefix: string = "heizcheck";

  /**
   * toggle for a loading animation on the next button.
   */
  loading: boolean = false;

  /**
   * list of backend errors to be shown on the frontend
   */
  backendErrors: string[];

  /** placeholders for the error messages that come from the backend.
   */
  backendErrorsPlaceholder: {[key: string]: string} = {};

  /**
   * List of errors for the single invoices.
   */
  invoiceErrors: {error: string, fields: string[]};

  /**
   * List of fields that are marked for a warning.
   */
  warningFields: string[] = [];

  /**
   * some placeholders for warning messages
   */
  warningPlaceholder: {[key: string]: string} = {};

  /**
   * holds all subheadlines of this step
   */
  subHeadlines: {[key: string]: string} = {};

  /**
   * toggle if at least one field was presetted
   */
  fieldsPresetted: boolean = false;

  /**
   * The wrapper for the stepform
   */
  @ViewChild('formWrapper') formWrapper: ElementRef;

  /**
   * @inheritDoc
   * @param stepService
   * @param router
   * @param requestService
   * @param elementService
   * @param portalService
   * @param translate
   * @param energyMapService
   */
  constructor(protected stepService: StepService, protected router: Router, protected requestService: HeizcheckRequestService,
              protected elementService: ElementService, protected portalService: PortalService, protected translate: TranslateService,
              protected energyMapService: EnergyMapService) {
    super(stepService, router, elementService);
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    this.anfrage = this.requestService.getAnfrage();

    // add the correct form config that we need in this steps and init the parent form
    this.formConfig = new HeizkostenabrechnungForm(this.energyMapService);
    this.formConfig.anfrage = this.anfrage;
    super.ngOnInit();

    // get the current input data and preset it
    this.formConfig.setData(this.anfrage.heizkostenanalyseInput);

    // update the previous step
    this.onStepUpdate();

    // set the msp as postfix for label and help texts
    this.formConfig.setPostfix(this.anfrage.heizkostenanalyseInput.messdienstleister);

    // init placeholder
    this.initBackendErrorPlaceholder();

    // preset detected values
    this.presetValuesFromDetection();
  }

  /**
   * Values in the form are preset with data from the backend.
   */
  presetValuesFromDetection(): void {
    // get the analytics result.
    const analysisResult = this.anfrage.detectedData;

    if(analysisResult && analysisResult['finished'] === true) {

      // get detected fields and the fieldnames
      const detectedValues = this.extractDetectedValues();
      const detectedFields = Object.keys(detectedValues);

      if(detectedFields.length < HeizcheckConstants.DETECTION_MIN_VALUECOUNT) {
        // stop here, we only want to show the result with at least 2 values
        return;
      }

      // percent pairs (Kostenverteilung) get completed; their form defaults are no user input
      const percentFieldsFromKi = this.completeDetectedPercentPairs(detectedValues);
      const presetFields = Object.keys(detectedValues);

      // filter all data that is already set by the user
      const existingData = this.formConfig.getData();
      const detectedValuesCopy = Object.assign({}, detectedValues);
      presetFields.forEach(field => {
        if(percentFieldsFromKi.includes(field)) {
          return;
        }
        if(existingData.hasOwnProperty(field) === true && existingData[field] !== null && existingData[field] !== '' && existingData[field] !== 0) {
          delete detectedValuesCopy[field];
        }
      });

      // preset data in the form
      this.formConfig.setData(detectedValuesCopy);

      // mark that we just have presetted
      for(const fieldName of presetFields) {
        const field = this.formConfig.getFieldsFromGroup(this.steps.currentStep.name).filter(field => field.getIds().indexOf(fieldName) > -1)[0];
        if(field && field.type !== "hidden") {
          // mark field as detected adn add extra image
          field.setPresetMark(true);
          if(detectedValues[fieldName]) {
            field.setExtraInfoImage({path: detectedValues[fieldName].path, marker: detectedValues[fieldName].marker});
          }
          this.fieldsPresetted = true;
        }
      }
    }
  }

  /**
   * The KI often detects only one part of a percent pair (e.g. 60 % Verbrauchskosten Heizung), while the
   * form presets both parts with a default (30/70). Adds the missing part (100 - value) to the detected
   * values and returns the field ids whose default may be replaced, because the user has not saved
   * own values for them yet.
   * @param detectedValues
   */
  protected completeDetectedPercentPairs(detectedValues: any): string[] {
    const savedInput = this.anfrage.heizkostenanalyseInput ?? {};
    const replaceableFieldIds: string[] = [];
    for (const field of this.formConfig.getFields().filter(formField => formField.type === 'percent')) {
      const [firstId, secondId] = field.getIds();
      const first = this.toPercent(detectedValues[firstId]?.value);
      const second = this.toPercent(detectedValues[secondId]?.value);
      if (first === null && second === null) {
        continue;
      }

      // use a complete pair if the KI found both parts, otherwise derive the missing part
      let values: number[];
      if (first !== null && second !== null && first + second === 100) {
        values = [first, second];
      } else if (second !== null) {
        values = [100 - second, second];
      } else {
        values = [first, 100 - first];
      }
      const source = detectedValues[secondId] ?? detectedValues[firstId];
      detectedValues[firstId] = {...source, value: values[0]};
      detectedValues[secondId] = {...source, value: values[1]};

      const savedByUser = [firstId, secondId].some(id => savedInput[id] !== undefined && savedInput[id] !== null && savedInput[id] !== '');
      if (!savedByUser) {
        replaceableFieldIds.push(firstId, secondId);
      }
    }

    return replaceableFieldIds;
  }

  /**
   * Returns the value as percent number (0-100) or null if it is no valid percent value.
   * @param value
   */
  protected toPercent(value: any): number | null {
    const percent = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
    return typeof percent === 'number' && isFinite(percent) && percent >= 0 && percent <= 100 ? percent : null;
  }

  /**
   * Returns all values from the ki analysis. Adds the confidence and a toggle if this is
   * a value changed by the user or not
   */
  protected extractDetectedValues(): any {
    const values = {};
    for (const page of Object.keys(this.anfrage.detectedData['fields'])) {
      for (const field of Object.keys(this.anfrage.detectedData['fields'][page])) {
        // get the value from the backend
        const value = this.anfrage.detectedData['fields'][page][field]['value'];
        values[field] = {
          value: value,
          confidence: this.anfrage.detectedData['fields'][page][field]['confidence'],
          marker: [this.anfrage.detectedData['fields'][page][field]['marker']],
          path: this.anfrage.detectedData.path + '/' + page + '.xml.png'
        };

        // special case with dates, they have to be splitted
        if(field === "abrechnungszeitraum" && value?.length === 2) {
          values['abrechnung_von'] = {...values[field], value: value[0]};
          values['abrechnung_bis'] = {...values[field], value: value[1]};
          for (const dateField of ['abrechnung_von', 'abrechnung_bis']) {
            if (values[dateField] && values[dateField].value.match(/[0-9]{4}-[0-9]{1,2}-[0-9]{1,2}/)) {
              const dateParts = values[dateField].value.split("-");
              values[dateField].value = dateParts[2] + '-' + dateParts[1] + '-' + dateParts[0];
            }
          }
        }
      }
    }

    // special case for the percentage values. If we do not detected the corressponding value, calculate it
    const subFieldPrefixes = ['prozent_grundkosten_', 'prozent_verbrauchskosten_'];
    ['warmwasser', 'raumwaerme'].forEach(fieldset => {
      if(values.hasOwnProperty(subFieldPrefixes[0] + fieldset) === true && values.hasOwnProperty(subFieldPrefixes[1] + fieldset) === false) {
        values[subFieldPrefixes[1] + fieldset] = {...values[subFieldPrefixes[0] + fieldset], value: 100 - values[subFieldPrefixes[0] + fieldset].value}
      }
      if(values.hasOwnProperty(subFieldPrefixes[1] + fieldset) === true && values.hasOwnProperty(subFieldPrefixes[0] + fieldset) === false) {
        values[subFieldPrefixes[0] + fieldset] = {...values[subFieldPrefixes[1] + fieldset], value: 100 - values[subFieldPrefixes[1] + fieldset].value}
      }
    });

    return values;
  }

  /**
   * @inheritDoc
   * @param data
   */
  saveData(data:{[key: string]: any}) {
    // merge the data with the existing data
    this.anfrage.heizkostenanalyseInput = {...this.anfrage.heizkostenanalyseInput, ...data};
    this.requestService.setAnfrage(this.anfrage);

    // save the data also to the server
    this.requestService.saveDataToServer();

    super.saveData(data);
  }

  /**
   * Return the next step from a step list
   * @param stepList
   */
  getNextStepFromList(stepList: Step[]): any {
    // set all data again in the form to avoid using old data for toggle fields
    if(this.anfrage?.heizkostenanalyseInput && this.formConfig && this.form) {
      this.formConfig.updateFields(this.anfrage.heizkostenanalyseInput);
      // save the data again. In some case some fields are autoupdatetd
      this.saveData({});
    }

    return super.getNextStepFromList(stepList);
  }

  /**
   * @inheritDoc
   */
  onSubmit(): void {
    const data = this.formConfig.getData();

    // overwrite some data form fields that can be toggled
    if(data.hasOwnProperty('gebaeudebaujahr')) {
      data.gebaeude_baualtersklasse_nutzerangabe = null;
    }
    if(data.hasOwnProperty('gebaeude_baualtersklasse_nutzerangabe')) {
      data.gebaeudebaujahr = null;
    }

    // save data
    this.saveData(data);

    /* some inputs end the advisor */
    if(this.isAdvisorEnd()) {
      // navigate to end page
      this.router.navigateByUrl(this.portalService.currentPortalId + "/heizcheck/heizkostenabrechnung/ausstieg", {state: {lastStep: this.router.url}});
      return;
    }

    //check if form is valid
    if(!this.form.valid) {
      // do nothing, form must be valid
      this.showFormErrors();
      return;
    }

    super.onSubmit();
  }

  /**
   * In some cases the advisor has to stop because we cannot offer any advice for this user/building
   * Hint: new cases have to be added also to HeizkostenabrechnungAusstiegComponent
   */
  isAdvisorEnd(): boolean {
    // case for 100% prozent_grundkosten_raumwaerme and prozent_grundkosten_warmwasser
    if(this.steps.currentStep?.name === 'kostendetails' && (this.anfrage.heizkostenanalyseInput?.prozent_grundkosten_raumwaerme === 100 ||
      this.anfrage.heizkostenanalyseInput?.prozent_grundkosten_warmwasser === 100)) {
      return true;
    }
    //case for "ja" for nutzergruppen
    if(this.steps.currentStep?.name === 'basisdaten/2' && this.anfrage.heizkostenanalyseInput?.nutzergruppen === "ja") {
      return true;
    }
    // not central heating
    return this.form.get("nutzungszeitraum") && this.form.get("nutzungszeitraum")?.value === "ja";
  }

  /**
   * @inheritDoc
   */
  onStepUpdate() {
    super.onStepUpdate();

    // overwrite the revious step on some steps because upload and foto should not be reachable when moving back.
    if(this.steps.currentStep?.name === 'basisdaten/1' || this.steps.currentStep?.name === 'start/upload' || this.steps.currentStep?.name === 'start/foto') {
      this.previousStep.path = '/' + this.portalService.currentPortalId + '/heizcheck/heizkostenabrechnung/start';
    }

    //set the result step as next step if 'heiznebenkosten_messgeraet_art' === "verdunster"
    if(this.anfrage.heizkostenanalyseInput.heiznebenkosten_messgeraet_art === "verdunster") {
      this.steps.nextStep = new Step('auswerten/heizkostenabrechnung', '/' + this.portalService.currentPortalId + '/heizcheck/auswerten/heizkostenabrechnung');
      this.steps.nextStepList.push(this.steps.nextStep);
    }

    // set the result step as next step if on the last one
    if(this.steps.currentStep?.name === 'sonstiges/3') {
      this.steps.nextStep = new Step('auswerten/heizkostenabrechnung', '/' + this.portalService.currentPortalId + '/heizcheck/auswerten/heizkostenabrechnung');
      this.steps.nextStepList.push(this.steps.nextStep);
    }

    // change the page helptext if value of 'warmwasser_bereitungsart' === "dezentral"
    if(this.steps.currentStep.name === 'sonstiges/2' && this.anfrage?.heizkostenanalyseInput?.warmwasser_bereitungsart === "dezentral") {
      this.helpTextkey += ".dezentral";
    }
  }

  /**
   * @inheritDoc
   */
  moveToNextStep(): void {
    if(this.steps.currentStep.name === 'sonstiges/3' || (this.steps.currentStep.name === 'sonstiges/1' && this.anfrage.heizkostenanalyseInput.heiznebenkosten_messgeraet_art === "verdunster")) {
      this.loading = true;
      // send data again to backend and show the result page
      this.requestService.backendService.getRequestId().subscribe(requestId => {
        const input = this.anfrage.heizkostenanalyseInput;

        this.requestService.backendService.heizkostenanalyse(input, requestId, true).subscribe(result => {
          this.loading = false;
          // save result
          this.anfrage.heizkostenanalyseResult = result;
          this.requestService.setAnfrage(this.anfrage);

          if (this.energyMapService.showEnergyMap(this.anfrage, 'heizcheck') === true
            && this.energyMapService.isEnergyMapDataDonated(this.anfrage) === false
            && this.energyMapService.getEnergyMapConfirmDonation(this.anfrage) !== 'n'
            && this.energyMapService.getEnergyMapBuildingNotIncludedInMap(this.anfrage) !== true
          ) {
            this.router.navigateByUrl(this.portalService.currentPortalId + '/heizcheck/heizkostenabrechnung/energy-map');
          }
          else {
            this.router.navigateByUrl(this.portalService.currentPortalId + "/heizcheck/auswerten/heizkostenabrechnung", {state: {lastStep: this.router.url}});
          }
        }, error => {
          this.loading = false;
          this.formError = 'connection';
          throw new Error('Could not (re)calculate data with energy bill API');
        });
      });
      return;
    }
    else {
      super.moveToNextStep();
    }
  }

  /**
   * Build some text placeholders for the (api) error messages
   */
  initBackendErrorPlaceholder(): any {
    let flaeche = 0;
    if(this.anfrage.heizkostenanalyseInput.hasOwnProperty('gebaeude_flaeche') && this.anfrage.heizkostenanalyseInput.gebaeude_flaeche > 0) {
      flaeche = this.anfrage.heizkostenanalyseInput.gebaeude_flaeche;
    }

    this.backendErrorsPlaceholder = {...this.anfrage.heizkostenanalyseInput, ...{portalId: this.portalService.currentPortalId}, ...{flaeche: flaeche, energietraeger: "test"}};

    // add some texts
    const energy = this.anfrage.heizkostenanalyseInput.energietraeger_name;
    this.backendErrorsPlaceholder['energietraeger'] = this.translate.instant('heizcheck.energietraeger_name.' + energy + '.label');
    this.backendErrorsPlaceholder['gebaeudebaujahr'] = this.anfrage.heizkostenanalyseInput.gebaeudebaujahr;

    return this.backendErrorsPlaceholder;
  }

  /**
   * Show error mark on affected fields of invalid forms
   * @param field
   */
  isAffectedFieldForError( field: FormModel): boolean {
    // check if at least on of the field ids is in the list of affected fields

    // remove all numbers from the fieldnames
    const fieldIds = field.getIds().map(id => id.replace(/\d/, ''));
    // remove some extra string from the affected fields
    const fieldNames = this.invoiceErrors?.fields? this.invoiceErrors.fields.map(fieldname => fieldname.replace('abrechnungen.0.', '').replace('.', '_')) : [];

    // check if one of the ids is in the affected field list
    return fieldIds.filter(id => fieldNames.indexOf(id) > -1).length > 0;
  }

  /**
   * Scrolls to the first (=highest) field in a list of fields
   * @param fieldIds
   */
  scrollToField(fieldIds: string[]): void {
    // find the highest field in the form
    const min = null;
    let field;
    fieldIds.forEach(fieldId => {
      if(document.getElementById(fieldId)) {
        const offset = document.getElementById(fieldId).offsetTop + document.documentElement.scrollTop;
        if(min === null || min > offset) {
          field = fieldId;
        }
      }
    });
    if(field) {
      // scroll upwards to this field
      this.elementService.scrollToElement(document.getElementById(field));
    }
    else {
      this.elementService.scrollToElement(this.stepForm.nativeElement);
    }
  }

  /**
   * Helper funtion to check if a fields should have a subhealine presetted.
   */
  initSubheadlines(): void {
    const groupedFields = this.formConfig.groupsConfig[this.anfrage.heizkostenanalyseInput.messdienstleister]?? this.formConfig.groupsConfig['default'];
    const fields = this.formConfig.getFieldsFromGroup(this.steps.currentStep.name);
    let lastHeadline: string = "";
    this.subHeadlines = {};
    fields.forEach(field => {
      Object.keys(groupedFields).forEach(section => {
        if(field.isActive() && groupedFields[section].indexOf(field.id) > -1 && section !== lastHeadline) {
          this.subHeadlines[field.id] = section;
          lastHeadline = section;
          return;
        }
      });
    });
  }

  /**
   * show form errors if the form should be submitted
   */
  showFormErrors(): void {
    this.formError = 'emptyfield';

    // check if there are formerrors set, if yes, show them
    if(this.form.errors) {
      this.formConfig.invalidateFieldsOfFormvalidators(this.form.errors);
      Object.keys(this.form.errors).map(key => this.formError = key);
    }
    // stop here, form is not valid
    if(this.formWrapper?.nativeElement) {
      this.elementService.scrollToElement(this.formWrapper.nativeElement);
    }
  }

}

